import React, { useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import { Button1, Checkbox, Select } from "../../../index";
import { bankAccountValidationApi, bsbApi } from "../../../../api/endpoints";

const COUNTRIES = [
  { name: "Australia", value: "AU" },
  { name: "United States", value: "US" },
  { name: "United Kingdom", value: "GB" },
  { name: "Pakistan", value: "PK" },
  { name: "India", value: "IN" },
  { name: "Canada", value: "CA" },
];
const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const validationColor = (result) => {
  if (!result) return "#7D7D7D";
  if (!result.canProceed) return "#FC555B";
  return result.warning ? "#B7791F" : "#1bbb6b";
};

const validationFromError = (e) => {
  const result = e.response?.data?.validation || e.response?.data;
  if (result?.status && result?.message) return result;
  return {
    canProceed: false,
    status: "unable_to_confirm",
    message: "We could not confirm the account name match.",
  };
};

const RecipientsInformation = ({
  accountType = "INDIVIDUAL",
  beneficiaryData,
  updateBeneficiary,
  onClick,
}) => {
  const isBusiness = accountType === "BUSINESS";

  // BSB validation only applies when the recipient bank is in Australia; for
  // other countries this field carries a routing/sort code instead.
  const isAuBank = beneficiaryData.bankCountry === "AU";
  const [bsbCheck, setBsbCheck] = useState({ loading: false, result: null });
  const [accountCheck, setAccountCheck] = useState({
    loading: false,
    result: null,
    key: "",
  });
  useEffect(() => {
    const code = digitsOnly(beneficiaryData.sortCode);
    if (!isAuBank || code.length !== 6) {
      setBsbCheck({ loading: false, result: null });
      return;
    }
    let cancelled = false;
    setBsbCheck({ loading: true, result: null });
    const t = setTimeout(() => {
      bsbApi
        .lookup(code)
        .then((r) => !cancelled && setBsbCheck({ loading: false, result: r }))
        .catch(() => !cancelled && setBsbCheck({ loading: false, result: null }));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [beneficiaryData.sortCode, isAuBank]);

  const validationName = String(
    beneficiaryData.accountName ||
      (isBusiness ? beneficiaryData.businessName : beneficiaryData.fullName) ||
      ""
  ).trim();
  const auAccountPayload = useMemo(
    () => ({
      accountName: validationName,
      bsb: digitsOnly(beneficiaryData.sortCode),
      accountNumber: digitsOnly(beneficiaryData.iban),
    }),
    [validationName, beneficiaryData.sortCode, beneficiaryData.iban]
  );
  const auAccountKey = `${auAccountPayload.accountName}|${auAccountPayload.bsb}|${auAccountPayload.accountNumber}`;

  const runAuAccountValidation = async () => {
    setAccountCheck({ loading: true, result: null, key: auAccountKey });
    try {
      const result = await bankAccountValidationApi.validateAustralia(auAccountPayload);
      setAccountCheck({ loading: false, result, key: auAccountKey });
      return result;
    } catch (e) {
      const result = validationFromError(e);
      setAccountCheck({ loading: false, result, key: auAccountKey });
      return result;
    }
  };

  useEffect(() => {
    if (
      !isAuBank ||
      !auAccountPayload.accountName ||
      auAccountPayload.bsb.length !== 6 ||
      auAccountPayload.accountNumber.length < 5 ||
      auAccountPayload.accountNumber.length > 12 ||
      bsbCheck.result?.valid === false
    ) {
      setAccountCheck({ loading: false, result: null, key: auAccountKey });
      return;
    }
    let cancelled = false;
    setAccountCheck({ loading: true, result: null, key: auAccountKey });
    const t = setTimeout(() => {
      bankAccountValidationApi
        .validateAustralia(auAccountPayload)
        .then((result) => {
          if (!cancelled) setAccountCheck({ loading: false, result, key: auAccountKey });
        })
        .catch((e) => {
          if (!cancelled) {
            setAccountCheck({
              loading: false,
              result: validationFromError(e),
              key: auAccountKey,
            });
          }
        });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    isAuBank,
    auAccountPayload,
    auAccountKey,
    bsbCheck.result?.valid,
  ]);

  const handleContinue = async () => {
    if (!isAuBank) {
      onClick(2);
      return;
    }
    if (!auAccountPayload.accountName) {
      updateBeneficiary("validationError", "Account name is required.");
      return;
    }
    if (auAccountPayload.bsb.length !== 6) {
      updateBeneficiary("validationError", "BSB must be 6 digits.");
      return;
    }
    if (
      auAccountPayload.accountNumber.length < 5 ||
      auAccountPayload.accountNumber.length > 12
    ) {
      updateBeneficiary("validationError", "Account number must be 5 to 12 digits.");
      return;
    }

    const result =
      accountCheck.result && accountCheck.key === auAccountKey
        ? accountCheck.result
        : await runAuAccountValidation();
    if (!result?.canProceed) {
      updateBeneficiary(
        "validationError",
        result?.message || "Please enter valid Australian bank account details."
      );
      return;
    }
    updateBeneficiary("validationError", "");
    onClick(2);
  };

  return (
    <>
      <div className="col-lg-6 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium">Bank Account Details</span>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">BSB/Routing/Sort Code</label>
            <Input
              className="p-3"
              placeholder="BSB, routing number, or sort code"
              value={beneficiaryData.sortCode}
              onChange={(e) => updateBeneficiary("sortCode", e.target.value)}
            />
            {isAuBank && bsbCheck.loading && (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                Checking BSB...
              </span>
            )}
            {isAuBank && bsbCheck.result?.valid && (
              <span className="fs12px fw-medium" style={{ color: "#1bbb6b" }}>
                {bsbCheck.result.bank}
                {bsbCheck.result.branch ? ` (${bsbCheck.result.branch})` : ""}
              </span>
            )}
            {isAuBank && bsbCheck.result && !bsbCheck.result.valid && (
              <span className="fs12px fw-medium" style={{ color: "#FC555B" }}>
                {bsbCheck.result.reason}
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">SWIFT/BIC Code</label>
            <Input
              className="p-3"
              placeholder="Your swift code"
              value={beneficiaryData.swiftCode}
              onChange={(e) => updateBeneficiary("swiftCode", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Bank Name</label>
            <Input
              className="p-3"
              placeholder="Your bank name"
              value={beneficiaryData.bankName}
              onChange={(e) => updateBeneficiary("bankName", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">IBAN/Account number</label>
            <Input
              className="p-3"
              placeholder="IBAN"
              value={beneficiaryData.iban}
              onChange={(e) => updateBeneficiary("iban", e.target.value)}
            />
            {isAuBank && accountCheck.loading ? (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                Confirming account details...
              </span>
            ) : isAuBank && accountCheck.result ? (
              <span
                className="fs12px fw-medium"
                style={{ color: validationColor(accountCheck.result) }}
              >
                {accountCheck.result.message}
              </span>
            ) : isAuBank ? (
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                Enter account name, BSB and account number to verify.
              </span>
            ) : null}
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Account Name</label>
            <Input
              className="p-3"
              placeholder="Your account name"
              value={beneficiaryData.accountName}
              onChange={(e) => updateBeneficiary("accountName", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Bank Country</label>
            <Select
              className="p-3"
              defaultValue="Select bank country"
              options={COUNTRIES}
              value={beneficiaryData.bankCountry}
              onChange={(e) => updateBeneficiary("bankCountry", e.target.value)}
            />
          </div>

          <div className="d-flex gap-2 align-items-center">
            <Checkbox
              checked={beneficiaryData.saveAsBeneficiary}
              onChange={(e) =>
                updateBeneficiary("saveAsBeneficiary", e.target.checked)
              }
            >
              Save as Beneficiary
            </Checkbox>
          </div>
          {beneficiaryData.validationError && (
            <span className="fs12px fw-medium" style={{ color: "#FC555B" }}>
              {beneficiaryData.validationError}
            </span>
          )}
        </div>
      </div>

      <div className="col-lg-5 p-3">
        <div className="d-flex flex-column gap-3">
          <span className="fs-5 fw-medium">
            {isBusiness ? "Business Details" : "Individual Details"}
          </span>

          {isBusiness ? (
            <div className="d-flex flex-column gap-2">
              <label className="fs12px">Business name</label>
              <Input
                className="p-3"
                placeholder="Your business name"
                value={beneficiaryData.businessName}
                onChange={(e) =>
                  updateBeneficiary("businessName", e.target.value)
                }
              />
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              <label className="fs12px">Full Name</label>
              <Input
                className="p-3"
                placeholder="e.g. Savannah Nguyen"
                value={beneficiaryData.fullName}
                onChange={(e) =>
                  updateBeneficiary("fullName", e.target.value)
                }
              />
            </div>
          )}

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">
              Nickname <span style={{ color: "#7D7D7D" }}>(Optional)</span>
            </label>
            <Input
              className="p-3"
              placeholder="Your nickname"
              value={beneficiaryData.nickname}
              onChange={(e) => updateBeneficiary("nickname", e.target.value)}
            />
            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              For your reference only. Nickname are only visible to you and will
              not be sent to recipient
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">
              Email <span style={{ color: "#7D7D7D" }}>(Optional)</span>
            </label>
            <Input
              className="p-3"
              placeholder="example@gmail.com"
              value={beneficiaryData.email}
              onChange={(e) => updateBeneficiary("email", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Address</label>
            <Input
              className="p-3"
              placeholder="Your address"
              value={beneficiaryData.address}
              onChange={(e) => updateBeneficiary("address", e.target.value)}
            />
            <span className="fs12px" style={{ color: "#7D7D7D" }}>
              Recipient's address (should not be a PO or GPO box address)
            </span>
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">City</label>
            <Input
              className="p-3"
              placeholder="Your city"
              value={beneficiaryData.city}
              onChange={(e) => updateBeneficiary("city", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">State</label>
            <Input
              className="p-3"
              placeholder="Your state"
              value={beneficiaryData.state}
              onChange={(e) => updateBeneficiary("state", e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Zipcode</label>
            <Input
              className="p-3"
              placeholder="Your zipcode"
              value={beneficiaryData.zipcode}
              onChange={(e) => updateBeneficiary("zipcode", e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-md-end  justify-content-center flex-wrap p-2 gap-3">
        <Button1
          buttonName={"Previous"}
          className={"border"}
          onClick={() => onClick(0)}
        />
        <Button1
          buttonName={"Save as Draft"}
          className={"btn-outline-success"}
        />
        <Button1
          buttonName={"Continue"}
          className="btn-dark "
          onClick={handleContinue}
        />
      </div>
    </>
  );
};

export default RecipientsInformation;
